import { useQuery } from '@tanstack/react-query';
import {
    DATE_FIELD,
    ETP_FIELD,
    HOURS_FIELD,
    SALARY_RAW_CHARGED_FIELD,
    TOTAL_HOURS_COST,
    TOTAL_HOURS_COUNT,
} from 'routes/HumanResourcesModule/business/definitions';
import { RQAPICallWithData } from 'store/APIClient';
import { payStateDataQueryKey } from './payStateData';

/**
 * Generic return type for all table indicators.
 */
type TableIndicators<T> = {
    currentExercice: T;
    previousExercice?: T;
};

/** The indicator data for Salary mass.
 */
export type TableSalaryMassIndicatorT = {
    [ETP_FIELD]?: number;
    [HOURS_FIELD]?: number;
    [SALARY_RAW_CHARGED_FIELD]: number;
    [DATE_FIELD]: string;
};

export type TableSalaryMassIndicatorsT =
    TableIndicators<TableSalaryMassIndicatorT>;

/** Return type for the EV table indicators. */
type TableSupCompIndicatorT = {
    [TOTAL_HOURS_COST]: number;
    [TOTAL_HOURS_COUNT]: number;
    [DATE_FIELD]: string;
};

export type TableSupCompIndicatorsT = TableIndicators<TableSupCompIndicatorT>;

type TablePayCompSalaryMassIndicatorT = {
    [SALARY_RAW_CHARGED_FIELD]: number;
    [DATE_FIELD]: string;
};

export type TablePayCompSalaryMassIndicatorsT =
    TableIndicators<TablePayCompSalaryMassIndicatorT>;

/** Hook to get the salary mass indicators.
 * @param payStateId - The ID of the pay state.
 * @param permanent - True for permanent agent, else false.
 * @returns Return a `SalaryMassIndicatorsT`. Note that the previous exercice data are optional
 *          and therefor should be properly handle as such.
 */
export const useGetTableSalaryMassIndicators = (
    payStateId: number,
    permanent: boolean,
) =>
    useQuery({
        queryKey: [
            payStateDataQueryKey(payStateId),
            'salary_mass_table_indicator',
            permanent,
        ],

        queryFn: async () => {
            const response =
                await RQAPICallWithData<TableSalaryMassIndicatorsT>({
                    endpoint: `human_resources/pay_state/${payStateId}/table_salary_mass_indicators?permanent=${permanent ? 1 : 0}`,
                });
            return response.content.data;
        },
    });

/** Hook to get the variable elements (sup/comp hours) indicators. */
export const useGetTableSupCompIndicators = (payStateId: number) =>
    useQuery({
        queryKey: [
            payStateDataQueryKey(payStateId),
            'sup_comp_table_indicator',
        ],

        queryFn: async () => {
            const response = await RQAPICallWithData<TableSupCompIndicatorsT>({
                endpoint: `human_resources/pay_state/${payStateId}/table_sup_comp_hours_indicators`,
            });
            return response.content.data;
        },
    });

/** Hook to get the pay component indicator. */
export const useGetTablePayCompSalaryMassIndicators = (
    payStateId: number,
    permanent: boolean,
) =>
    useQuery({
        queryKey: [
            payStateDataQueryKey(payStateId),
            'pay_comp_salary_mass_table_indicator',
            permanent,
        ],

        queryFn: async () => {
            const response =
                await RQAPICallWithData<TablePayCompSalaryMassIndicatorsT>({
                    endpoint: `human_resources/pay_state/${payStateId}/table_pay_component_salary_mass_indicators?permanent=${permanent ? 1 : 0}`,
                });
            return response.content.data;
        },
    });
